// Dependencies
import React, { useState, useEffect } from 'react';

import { connect } from 'react-redux';
import numeral from 'numeral';

import { ElementImpression } from '@nerdwallet/nw-client-lib/tracking';

// Lib
import {
  getTransitRating,
  getDiversityRating,
} from '../../../../config/city-life/get-rating-config';
import resultsBodyConfig from '../../../../config/city-life/results-body-config';
import buildCityStateString from '../../../../lib/buildCityStateString';

// Local Components
import { ACTIVE_TAB_CHANGE } from '../../../../reducers/cl/city-life';
import ClResultsTable from '../cl-results-table/cl-results-table';

// CSS
require('./cl-results-body.less');

// Constants
const offset = 50; // number of pixels we want to leave above title when scrolling to it

type ClResultsBodyProps = {
  activeTitleIndex: number;
  activeTabIndex?: number;
  dispatch: (any) => void;
  onCompareClick: (any) => void;
  cityData: Record<string, any>;
  totalNumCities: number;
};

const ClResultsBody = ({
  activeTitleIndex,
  activeTabIndex,
  dispatch,
  onCompareClick,
  cityData,
  totalNumCities,
}: ClResultsBodyProps): JSX.Element => {
  const [titles] = useState([]);

  const handleScroll = () => {
    let minTop = Number.MAX_SAFE_INTEGER;
    let minIndex = -1;

    // find section that has the smallest top to find section that user is currently on
    titles.forEach((title, index) => {
      const top = title.getBoundingClientRect().top - offset;

      // find which number is closer to 0
      if (Math.abs(top) < minTop) {
        minTop = Math.abs(top);
        minIndex = index;
      }
    });

    // remove all active tabs and only make element we found above active
    if (activeTabIndex !== minIndex) {
      dispatch({
        type: ACTIVE_TAB_CHANGE,
        activeTabIndex: minIndex,
      });
    }
  };

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      if (activeTitleIndex >= 0 && activeTitleIndex !== activeTabIndex) {
        // top relative to where we are on the page
        const top = titles[activeTitleIndex].getBoundingClientRect().top;
        window.scrollTo({
          top: top + window.scrollY - offset,
          behavior: 'smooth',
        });
      }
    }
  }, [activeTitleIndex, activeTabIndex, titles]);

  const renderResultsTables = ({ modifiedCityData }) => {
    return resultsBodyConfig.map((config, index) => [
      <ElementImpression
        key={config.title}
        id={`${config.id}`}
        elementName={`cl-results-title-${config.id}`}
      >
        <div
          className="cl-results-title"
          ref={(node) => {
            titles[index] = node;
          }}
        >
          <h2 className="cl-results-title-text">{config.title}</h2>
        </div>
      </ElementImpression>,
      config.config ? (
        <ClResultsTable
          key={config.id}
          trackingFormName={`cl-${config.id}-results-table`}
          data={modifiedCityData}
          // @ts-ignore type error for viewConfig
          viewConfig={config.config}
        />
      ) : null,
      // eslint-disable-next-line no-nested-ternary
      config.children
        ? config.id === 'compare'
          ? config.children({
              cityData: modifiedCityData,
              // @ts-ignore type error for onCompareClick
              onCompareClick,
              index,
            })
          : config.children({ cityData: modifiedCityData })
        : null,
    ]);
  };

  const adjustedIncome = numeral(cityData?.median_salary).format('$0,0');
  const ranking = cityData?.ranking;
  const sup = numeral(ranking).format('0o').slice(-2);

  // values that have custom formatting not supported by config builder
  const cityState = buildCityStateString(cityData);
  const greatschoolsRating = `${cityData.greatschools_rating} / 10`;
  const ratioUnmarriedMenWomen = `${cityData.ratio_unmarried_men_women} / 100`;
  const diversityRating = `${Math.round(
    cityData.diversity_index
  )} - ${getDiversityRating(cityData.diversity_index)}`;
  const walkscore = `${cityData.walkscore} - ${cityData.walkscore_explanation}`;
  const publicTransitRating = `${
    cityData.public_transit_score
  } - ${getTransitRating(cityData.public_transit_score)}`;
  const privateSchoolTuitionCost = `${numeral(
    cityData.private_school_tuition_cost
  ).format('$0,0')} / year`;
  const population = numeral(cityData.population).format('0,0');
  const averageTravelTime = `${numeral(
    cityData.average_travel_time_to_work_min
  ).format('0.0')} minutes`;

  const modifiedCityData = {
    ...cityData,
    cityState,
    greatschools_rating: greatschoolsRating,
    ratio_unmarried_men_women: ratioUnmarriedMenWomen,
    diversity_rating: diversityRating,
    walkscore,
    public_transit_rating: publicTransitRating,
    private_school_tuition_cost: privateSchoolTuitionCost,
    population,
    average_travel_time_to_work_min: averageTravelTime,
  };

  return (
    <div className="cl-results">
      <div className="cl-results-header">
        <div className="cl-results-header-title">
          The median salary in {cityState} is:
        </div>
        <div className="cl-results-header-income col-results-important">
          {adjustedIncome}
        </div>
        <div className="cl-results-header-info">
          {cityState} is the {ranking}
          <sup>{sup}</sup> most expensive city of database of {totalNumCities}{' '}
          cities
        </div>
      </div>

      {renderResultsTables({
        modifiedCityData,
      })}

      <h3 className="cl-results-col-link">
        You can view more comparative information on how {cityState} compares to
        other cities by visiting our
        <a href="/cost-of-living-calculator"> Cost of Living Calculator.</a>
      </h3>
    </div>
  );
};

ClResultsBody.displayName = 'ClResultsBody';

const mapStateToProps = (state) => ({
  activeTabIndex: state?.activeTabIndex,
});
export default connect(mapStateToProps)(ClResultsBody);
